import React, { useEffect, useRef } from 'react';

const DURATION_MS = 15_000;

/**
 * Celebratory fireworks for admin new-signup alerts (~15s) with synthesized sound.
 * Self-clears via onDone when the show ends.
 */
export default function SignupFireworks({ active, onDone }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let raf = 0;
    let cancelled = false;
    const particles = [];
    const timers = [];
    const colors = ['#34d399', '#fbbf24', '#60a5fa', '#f472b6', '#a78bfa', '#fb7185', '#f97316', '#e879f9'];

    let audioCtx = null;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        audioCtx = new AC();
        if (audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => {});
        }
      }
    } catch {
      audioCtx = null;
    }

    const playBoom = (strength = 1) => {
      if (!audioCtx || cancelled) return;
      try {
        const t0 = audioCtx.currentTime;
        const dur = 0.28 + Math.random() * 0.18;

        // Filtered noise crack
        const bufferSize = Math.floor(audioCtx.sampleRate * dur);
        const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i += 1) {
          const env = Math.pow(1 - i / bufferSize, 2.4);
          data[i] = (Math.random() * 2 - 1) * env;
        }
        const noise = audioCtx.createBufferSource();
        noise.buffer = buffer;
        const filter = audioCtx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 900 + Math.random() * 1800;
        filter.Q.value = 0.7;
        const noiseGain = audioCtx.createGain();
        noiseGain.gain.setValueAtTime(0.0001, t0);
        noiseGain.gain.exponentialRampToValueAtTime(0.35 * strength, t0 + 0.012);
        noiseGain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(audioCtx.destination);
        noise.start(t0);
        noise.stop(t0 + dur + 0.02);

        // Soft tonal pop
        const osc = audioCtx.createOscillator();
        const oscGain = audioCtx.createGain();
        osc.type = 'triangle';
        const base = 180 + Math.random() * 220;
        osc.frequency.setValueAtTime(base * 2.2, t0);
        osc.frequency.exponentialRampToValueAtTime(base * 0.55, t0 + 0.22);
        oscGain.gain.setValueAtTime(0.0001, t0);
        oscGain.gain.exponentialRampToValueAtTime(0.18 * strength, t0 + 0.01);
        oscGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
        osc.connect(oscGain);
        oscGain.connect(audioCtx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.35);
      } catch {
        /* ignore autoplay / audio errors */
      }
    };

    const playWhistle = () => {
      if (!audioCtx || cancelled) return;
      try {
        const t0 = audioCtx.currentTime;
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(420 + Math.random() * 80, t0);
        osc.frequency.exponentialRampToValueAtTime(1200 + Math.random() * 400, t0 + 0.35);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.08, t0 + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.42);
      } catch {
        /* ignore */
      }
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const burst = (ox, oy, count = 48, withSound = true) => {
      if (withSound) playBoom(0.75 + Math.random() * 0.5);
      for (let i = 0; i < count; i += 1) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.25;
        const speed = 2.0 + Math.random() * 5.2;
        particles.push({
          x: ox,
          y: oy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - Math.random() * 1.8,
          life: 1,
          decay: 0.006 + Math.random() * 0.008,
          color: colors[i % colors.length],
          size: 1.6 + Math.random() * 2.8,
        });
      }
    };

    const scheduleBurst = (delayMs) => {
      timers.push(setTimeout(() => {
        if (cancelled) return;
        const w = window.innerWidth;
        const h = window.innerHeight;
        playWhistle();
        timers.push(setTimeout(() => {
          if (cancelled) return;
          burst(
            w * (0.18 + Math.random() * 0.64),
            h * (0.18 + Math.random() * 0.42),
            40 + Math.floor(Math.random() * 28),
            true,
          );
        }, 280));
      }, delayMs));
    };

    // Opening salvo + ongoing show for ~15s
    const w0 = window.innerWidth;
    const h0 = window.innerHeight;
    playWhistle();
    timers.push(setTimeout(() => {
      if (!cancelled) burst(w0 * 0.5, h0 * 0.3, 64, true);
    }, 250));
    scheduleBurst(900);
    scheduleBurst(1800);
    scheduleBurst(2900);
    scheduleBurst(4200);
    scheduleBurst(5600);
    scheduleBurst(7200);
    scheduleBurst(8800);
    scheduleBurst(10500);
    scheduleBurst(12200);
    scheduleBurst(13800);

    const started = performance.now();
    const tick = (now) => {
      if (cancelled) return;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.vy += 0.055;
        p.vx *= 0.995;
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      const elapsed = now - started;
      if (elapsed < DURATION_MS || particles.length) {
        raf = requestAnimationFrame(tick);
      } else {
        onDone?.();
      }
    };
    raf = requestAnimationFrame(tick);

    // Hard stop so onDone always fires even if particles linger.
    timers.push(setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      cancelAnimationFrame(raf);
      try { ctx.clearRect(0, 0, window.innerWidth, window.innerHeight); } catch { /* ignore */ }
      onDone?.();
    }, DURATION_MS + 800));

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      timers.forEach((id) => clearTimeout(id));
      try { audioCtx?.close(); } catch { /* ignore */ }
    };
  }, [active, onDone]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[1100]"
      aria-hidden="true"
    />
  );
}
